import { colors, COLORS } from '@constants/colors';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

export const useAiScriptsTreeStyles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    // Fill the rail body so the header/footer can pin and only the list
    // between them scrolls.
    height: '100%',
    minHeight: 0,
  },
  // The single scrolling region: trigger/knowledge rows live here while the
  // mode switcher + follow/filter row (above) and the create button (below)
  // stay fixed.
  scrollArea: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflowY: 'auto',
    paddingRight: '2px',
    scrollbarWidth: 'thin',
    scrollbarColor: '#FFFFFF4D transparent',
    '&::-webkit-scrollbar': {
      width: '8px',
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#FFFFFF4D',
      border: '2px solid transparent',
      backgroundClip: 'padding-box',
      borderRadius: '18px',
    },
  },

  filterChipsRow: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    padding: '0 4px 10px',
  },
  filterChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11.5px',
    fontWeight: 650,
    color: colors.white_60,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    borderRadius: '999px',
    padding: '5px 11px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    minHeight: 0,
    lineHeight: 1.2,
    fontFamily: 'inherit',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
      color: COLORS.WHITE,
    },
  },
  filterChipActive: {
    backgroundColor: 'rgba(110, 108, 246, 0.22)',
    borderColor: 'rgba(110, 108, 246, 0.5)',
    color: '#e3e3ff',
  },
  filterChipCount: {
    color: colors.white_50,
    fontWeight: 700,
    marginLeft: '3px',
  },
  filterChipCountIssues: {
    color: '#f06d6d',
  },

  rowList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  sectionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 6px 4px',
    fontSize: '10px',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#7d828e',
  },
  sectionCount: {
    color: '#62666f',
    fontWeight: 700,
    marginLeft: 'auto',
  },
  treeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    width: '100%',
    padding: '8px 9px',
    borderRadius: '11px',
    border: 'none',
    backgroundColor: 'transparent',
    color: COLORS.WHITE,
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: 560,
    fontFamily: 'inherit',
    transition: 'background-color 0.12s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.055)',
    },
  },
  treeRowSelected: {
    backgroundColor: 'rgba(110, 108, 246, 0.22)',
    boxShadow: 'inset 0 0 0 1px rgba(110, 108, 246, 0.38)',
    // Keep the purple selection while hovering — otherwise `.treeRow:hover`
    // (higher specificity) swaps the fill for the neutral wash.
    '&:hover': {
      backgroundColor: 'rgba(110, 108, 246, 0.28)',
    },
  },
  rowIcon: {
    color: colors.white_60,
    flex: 'none',
    fontSize: 16,
  },
  rowName: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  rowTitle: {
    fontSize: '13px',
    fontWeight: 560,
    color: COLORS.WHITE,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowSubtitle: {
    fontFamily: MONO,
    fontSize: '10px',
    color: '#787d89',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  emptyState: {
    color: colors.white_40,
    fontSize: '12px',
    padding: '12px 8px',
    fontStyle: 'italic',
    textAlign: 'center',
  },

  // Mode switcher (Spot / AI trigger / Knowledge). The active segment is tinted
  // inline with the per-kind tokens (see kind.ts).
  modeSwitcher: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '5px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '13px',
    padding: '4px',
    margin: '0 2px 9px',
  },
  modeSeg: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '5px',
    padding: '8px 4px 7px',
    borderRadius: '10px',
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    color: '#7d828e',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
    '& svg': {
      fontSize: 16,
    },
    '&:hover': {
      color: COLORS.WHITE,
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
    },
  },
  modeSegLabel: {
    fontSize: '9.5px',
    fontWeight: 600,
    letterSpacing: '0.03em',
    lineHeight: 1,
  },
  modeHint: {
    fontSize: '10.5px',
    lineHeight: 1.45,
    color: '#7d828e',
    padding: '0 4px',
    margin: '0 0 9px',
    minHeight: '30px',
  },

  // "Follow live trigger" switch row (AI-trigger mode).
  followRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '4px 6px 10px',
    mb: 1,
  },
  followText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  followTitle: {
    fontSize: '11.5px',
    fontWeight: 650,
    color: COLORS.WHITE,
    lineHeight: 1.3,
  },
  followSub: {
    fontSize: '10px',
    color: '#7d828e',
    lineHeight: 1.35,
  },
  followSwitch: {
    flex: 'none',
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#8B7AF8',
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: 'rgba(139, 122, 248, 0.6)',
    },
  },
  // Subtle accent on the row matching the live UE trigger (trigger mode,
  // inspector closed).
  treeRowLive: {
    boxShadow: 'inset 0 0 0 1px rgba(139, 122, 248, 0.5)',
  },

  // Collapsible section header (Spot mode: Configured / Empty).
  collapsibleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
    padding: '8px 6px 5px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    color: '#7d828e',
    transition: 'color 0.12s ease',
    '&:hover': {
      color: '#a7abb8',
    },
  },
  collapsibleHeaderText: {
    fontSize: '10px',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  collapsibleCount: {
    color: '#62666f',
    fontWeight: 700,
    marginLeft: 'auto',
    fontSize: '11px',
  },
  collapsibleChevron: {
    fontSize: 15,
    color: '#62666f',
    transition: 'transform 0.15s ease',
    flex: 'none',
  },
  collapsibleChevronCollapsed: {
    transform: 'rotate(-90deg)',
  },
  sectionEmptyHint: {
    color: '#62666f',
    fontSize: '11px',
    fontStyle: 'italic',
    padding: '4px 10px 8px',
  },
  sectionHeaderAmber: {
    color: '#f0b357',
  },
  sectionHeaderRed: {
    color: '#ef8b86',
  },
  driftRowBadge: {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    padding: '1px 5px',
    borderRadius: '5px',
    backgroundColor: 'rgba(124, 108, 240, 0.22)',
    color: '#c8bdff',
    flex: 'none',
  },

  footer: {
    padding: '8px 4px 4px',
  },
  createButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    border: '1px dashed rgba(255, 255, 255, 0.22)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    color: '#d4d6df',
    borderRadius: '12px',
    padding: '9px',
    fontSize: '12.5px',
    fontWeight: 650,
    cursor: 'pointer',
    textTransform: 'none',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: 'rgba(110, 108, 246, 0.5)',
      color: COLORS.WHITE,
      backgroundColor: 'rgba(110, 108, 246, 0.08)',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
});
